


import { useEffect, useState } from "react";

import {
  ShieldCheck,
  QrCode,
  Copy,
  CheckCircle2,
  KeyRound,
  RefreshCw,
  Lock,
  ArrowRight,
  AlertCircle,
  Trash2,
  Smartphone,
  ShieldAlert,
  ShieldX,
} from "lucide-react"; // Or use your preferred icon package/SVGs
import { disableAuthenticator, generateAuthQR, getAdminProfile, verifyAuthenticatorCode } from "../../services/authService";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { toast } from "react-toastify";

function AuthenticatorSettings() {
  const [QRResponse, setQRResponse] = useState(null); // Expects base64 string or object with image & secret
  const [authCode, setAuthCode] = useState("");
  const [disableCode, setDisableCode] = useState(""); // 6-digit code for disabling 2FA
  const [isGenerating, setIsGenerating] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [copied, setCopied] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [disableErrorMessage, setDisableErrorMessage] = useState("");
  const [profileData, setProfileData] = useState(null);
  const [isGloballyEnabled, setIsGloballyEnabled] = useState(false)

  // Extract QR base64 data & secret key safely regardless of API structure
  const qrImageSrc =
    typeof QRResponse === "string"
      ? QRResponse
      : QRResponse?.qr_code || QRResponse?.image || QRResponse?.qrCode || "";
  const secretKey = QRResponse?.secret || QRResponse?.secretKey || "";

  const getAuthenticatorQR = async () => {
    if (!profileData) return showSuccessToast("Profile data not Found");
    setIsGenerating(true);
    setErrorMessage("");
    try {
      const res = await generateAuthQR();
            console.log(res);

      if (res?.qr_code) {
        setQRResponse(res);
        showSuccessToast("Authenticator QR generated successfully");
      } else {
        showErrorToast(
          res?.message || res?.data?.message || "Failed to generate QR Code",
        );
      }
    } catch (error) {
      console.error(error);
      showErrorToast("An error occurred while generating QR Code");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6); // Keep max 6 numeric digits
    setAuthCode(val);
    if (errorMessage) setErrorMessage("");
  };

  const handleDisableCodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setDisableCode(val);
    if (disableErrorMessage) setDisableErrorMessage("");
  };

  const verifyCode = async (e) => {
    e?.preventDefault();
    if (authCode.length !== 6) {
      setErrorMessage("Please enter a valid 6-digit authenticator code.");
      return;
    }

    setIsVerifying(true);
    setErrorMessage("");
    try {
      const res = await verifyAuthenticatorCode({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        code: authCode,
      });
      console.log(res);
      
      if ( res?.status_code === 200) {
        setIsVerified(true);
        showSuccessToast(
          res?.message ||
            res?.data?.message ||
            res?.data?.data?.message ||
            "2FA setup completed successfully!",
        );
      } else {
        const errorMsg =
          res?.message || res?.data?.message || "Invalid authentication code";
        setErrorMessage(errorMsg);
        showErrorToast(errorMsg);
      }
    } catch (error) {
      console.error(error);
      const errText =
        error?.response?.data?.message ||
        "Verification failed. Please try again.";
      setErrorMessage(errText);
      showErrorToast(errText);
    } finally {
      setIsVerifying(false);
    }
  };

  // Handler to disable authenticator including the 6-digit code in payload
  const handleDisableAuthenticator = async (e) => {
    e?.preventDefault();
    if (!profileData) return showErrorToast("Profile data not found");
    if (disableCode.length !== 6) {
      setDisableErrorMessage(
        "Please enter a valid 6-digit code to disable 2FA.",
      );
      return;
    }

    setIsDisabling(true);
    setDisableErrorMessage("");
    try {
      const res = await disableAuthenticator({
        code: disableCode,
      });
      console.log(res);

      // Adjust success condition based on your backend response structure
      if ( res?.status_code === 200 || res?.data?.status_code === 200) {
        setIsVerified(false);
        setQRResponse(null);
        setAuthCode("");
        setDisableCode("");
        showSuccessToast(
          res?.message ||
            res?.data?.message ||
            "Authenticator disabled successfully",
        );
      } else {
        const errorMsg =
          res?.message ||
          res?.data?.message ||
          res?.data?.data?.message ||
          "Failed to disable authenticator";
        setDisableErrorMessage(errorMsg);
        showErrorToast(errorMsg);
      }
    } catch (error) {
      console.error(error);
      const errText =
        error?.response?.data?.message ||
        "Failed to disable authenticator. Please try again.";
      setDisableErrorMessage(errText);
      showErrorToast(errText);
    } finally {
      setIsDisabling(false);
    }
  };

  const copyToClipboard = () => {
    if (secretKey) {
      navigator.clipboard.writeText(secretKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await getAdminProfile();
        setProfileData(res?.data || null);
        setIsVerified(res?.google_authenticator?.is_enabled);
        setIsGloballyEnabled(res?.google_authenticator?.is_globally_enabled)
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      }
    };

    loadProfile();
  }, []);


  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">

      <main className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Top Header Card */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
              <ShieldCheck className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                Two-Factor Authentication (2FA)
              </h2>
              <p className="text-slate-600 text-sm mt-1">
                Protect your account by enabling Google Authenticator, Authy, or
                any compatible 2FA app.
              </p>
            </div>
          </div>
        </div>

        {/* Setup Flow Card */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">

{/* Card Header Container */}
<div className="border-b border-slate-100 bg-slate-50/50 p-6 space-y-4">
  
  {/* Top Row: Title, Subtitle & Badges */}
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
    <div>
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-slate-900 text-lg">
          Authenticator App Setup
        </h3>
        
        {/* Global Admin Status Pill */}
        {isGloballyEnabled ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200/80">
            <ShieldCheck className="w-3 h-3 text-blue-500" /> Global 2FA Allowed
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[11px] font-medium bg-rose-50 text-rose-700 border border-rose-200/80">
            <ShieldX className="w-3 h-3 text-rose-500" /> Disabled by Admin
          </span>
        )}
      </div>

      <p className="text-xs text-slate-500 mt-1">
        {!isGloballyEnabled
          ? "Authenticator setup is currently restricted across the platform."
          : isVerified
          ? "2FA is active and protecting your account."
          : "Follow the steps below to connect your app."}
      </p>
    </div>

    {/* User Verification Status Badge */}
    <div className="flex items-center gap-2 self-start sm:self-auto">
      {isVerified ? (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/80 shadow-xs">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Configured & Verified
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200/80 shadow-xs">
          <Lock className="w-3.5 h-3.5 text-amber-600" /> Setup Required
        </span>
      )}
    </div>
  </div>

  {/* Admin Blocked Warning Banner (Shown when not globally enabled and not verified) */}
  {!isGloballyEnabled && !isVerified && (
    <div className="flex items-start gap-3 p-3.5 rounded-xl bg-amber-50/80 border border-amber-200/90 text-amber-900 transition-all animate-in fade-in slide-in-from-top-1">
      <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
      <div className="text-xs leading-relaxed">
        <span className="font-semibold text-amber-950">2FA Setup Restricted:</span>{" "}
        You cannot enable Google Authenticator because it is currently disabled globally by the system administrator. Please contact your administrator for assistance.
      </div>
    </div>
  )}

</div>
          <div className="p-6 space-y-8">
            {/* If Verified, show Disable Authenticator Section */}
            {isVerified ? (
              <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white font-bold text-xs">
                    ✓
                  </span>
                  <h4 className="font-medium text-slate-900">
                    Disable Two-Factor Authentication
                  </h4>
                </div>
                <p className="text-sm text-slate-600">
                  To turn off 2FA, please enter your current 6-digit
                  authenticator code below.
                </p>

                <form
                  onSubmit={handleDisableAuthenticator}
                  className="max-w-md space-y-3"
                >
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={disableCode}
                      onChange={handleDisableCodeChange}
                      placeholder="000000"
                      className={`w-full tracking-[0.5em] text-center font-mono text-xl py-3 px-4 rounded-lg border bg-white focus:outline-none transition-all ${
                        disableErrorMessage
                          ? "border-red-500 focus:ring-2 focus:ring-red-200"
                          : "border-slate-300 focus:border-red-600 focus:ring-2 focus:ring-red-100"
                      } text-slate-900`}
                    />
                  </div>

                  {disableErrorMessage && (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />{" "}
                      {disableErrorMessage}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={disableCode.length !== 6 || isDisabling}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 shadow-sm disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
                  >
                    {isDisabling ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" /> Disabling
                        Authenticator...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" /> Disable Authenticator
                      </>
                    )}
                  </button>
                </form>
              </div>
            ) : (
              <>
                {/* Step 1: Generate & Scan QR Code */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white font-bold text-xs">
                        1
                      </span>
                      <h4 className="font-medium text-slate-900">
                        Get & Scan QR Code
                      </h4>
                    </div>

                    {!qrImageSrc && (
                      <button
                        onClick={getAuthenticatorQR}
                        disabled={isGenerating}
                        type="button"
                        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isGenerating ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />{" "}
                            Generating...
                          </>
                        ) : (
                          <>
                            <QrCode className="w-4 h-4" /> Generate QR Code
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* QR Image Display Area */}
                  {qrImageSrc && (
                    <div className="mt-4 p-5 bg-slate-50 border border-slate-200 rounded-xl flex flex-col md:flex-row items-center gap-6">
                      {/* Base64 Image Render */}
                      <div className="w-44 h-44 bg-white p-2.5 rounded-lg border border-slate-200 shadow-sm flex items-center justify-center relative">
                        <img
                          className="object-contain w-full h-full rounded"
                          src={
                            qrImageSrc.startsWith("data:")
                              ? qrImageSrc
                              : `data:image/svg+xml;base64,` + qrImageSrc
                          }
                          alt="Authenticator QR Code"
                        />
                      </div>

                      <div className="flex-1 space-y-3 text-center md:text-left">
                        <p className="text-sm text-slate-600">
                          Open Google Authenticator, Authy, or Microsoft
                          Authenticator on your mobile device and scan this QR
                          code.
                        </p>

                        {/* Manual Secret Key Fallback */}
                        {secretKey && (
                          <div className="pt-2">
                            <span className="text-xs text-slate-500 font-medium block mb-1">
                              Can't scan? Enter key manually:
                            </span>
                            <div className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-md border border-slate-200 font-mono text-xs text-slate-700 font-semibold">
                              <KeyRound className="w-3.5 h-3.5 text-slate-400" />
                              <span>{secretKey}</span>
                              <button
                                type="button"
                                onClick={copyToClipboard}
                                className="ml-1 text-blue-600 hover:text-blue-800 transition-colors"
                                title="Copy Key"
                              >
                                {copied ? (
                                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                ) : (
                                  <Copy className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>
                          </div>
                        )}

                        <div>
                          <button
                            onClick={getAuthenticatorQR}
                            type="button"
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium underline inline-flex items-center gap-1 mt-1"
                          >
                            <RefreshCw className="w-3 h-3" /> Regenerate QR Code
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <hr className="border-slate-100" />

                {/* Step 2: Verification Code Input Form */}
                <form onSubmit={verifyCode} className="space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white font-bold text-xs">
                      2
                    </span>
                    <label
                      htmlFor="code"
                      className="font-medium text-slate-900 cursor-pointer"
                    >
                      Enter 6-Digit Authenticator Code
                    </label>
                  </div>

                  <div className="max-w-md space-y-3">
                    <div className="relative">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        name="code"
                        id="code"
                        value={authCode}
                        onChange={handleCodeChange}
                        placeholder="000000"
                        disabled={!qrImageSrc || isVerified}
                        className={`w-full tracking-[0.5em] text-center font-mono text-xl py-3 px-4 rounded-lg border bg-white focus:outline-none transition-all ${
                          errorMessage
                            ? "border-red-500 focus:ring-2 focus:ring-red-200"
                            : "border-slate-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
                        } ${!qrImageSrc || isVerified ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "text-slate-900"}`}
                      />
                    </div>

                    {errorMessage && (
                      <p className="text-xs text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" /> {errorMessage}
                      </p>
                    )}

                    {/* Primary Action Button (Blue Foreground) */}
                    <button
                      type="submit"
                      disabled={
                        authCode.length !== 6 || isVerifying || isVerified
                      }
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 shadow-sm disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
                    >
                      {isVerifying ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />{" "}
                          Verifying Code...
                        </>
                      ) : isVerified ? (
                        <>
                          <CheckCircle2 className="w-4 h-4" /> Authenticated
                        </>
                      ) : (
                        <>
                          Authenticate Now <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </main>

      <TwoFAInstructions />
    </div>
  );
}

export default AuthenticatorSettings;

function TwoFAInstructions() {
  const steps = [
    {
      icon: <Smartphone className="w-5 h-5 text-blue-600" />,
      stepNum: "1",
      title: "Download an Authenticator App",
      description:
        "Install a 2FA authenticator app on your smartphone if you haven't already. We recommend Google Authenticator, Microsoft Authenticator, or Authy (available on iOS and Android app stores).",
    },
    {
      icon: <QrCode className="w-5 h-5 text-blue-600" />,
      stepNum: "2",
      title: "Generate & Scan the QR Code",
      description:
        "Go to your Login Security page, click 'Generate QR Code', and open your authenticator app. Use your phone's camera or the app scanner to scan the QR code displayed on screen.",
    },
    // {
    //   icon: <KeyRound className="w-5 h-5 text-blue-600" />,
    //   stepNum: "3",
    //   title: "Save Your Manual Setup Key (Optional)",
    //   description:
    //     "If your phone's camera cannot scan the QR code, copy the 16-character backup secret key provided below the QR image and type it manually into your authenticator app.",
    // },
    {
      icon: <CheckCircle2 className="w-5 h-5 text-emerald-600" />,
      stepNum: "3",
      title: "Enter the 6-Digit Code to Verify",
      description:
        "Your authenticator app will now generate a rotating 6-digit security code. Type that 6-digit code into Step 2 on the security page and click 'Authenticate Now' to complete setup.",
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 pb-6 border-b border-slate-100">
        <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
          <ShieldCheck className="w-7 h-7" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-slate-900">
            How to Set Up Two-Factor Authentication (2FA)
          </h3>
          <p className="text-xs text-slate-500">
            Follow these quick steps to safeguard your account against
            unauthorized access.
          </p>
        </div>
      </div>

      {/* Steps List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6">
        {steps.map((item, index) => (
          <div
            key={index}
            className="p-5 rounded-xl bg-slate-50 border border-slate-200/60 flex items-start gap-4 transition-all hover:bg-slate-100/60"
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white shadow-sm border border-slate-200 shrink-0 font-bold text-xs text-blue-600">
              {item.stepNum}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                {item.icon}
                <h4 className="font-semibold text-sm text-slate-900">
                  {item.title}
                </h4>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                {item.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Security Tip Footer */}
      <div className="mt-6 p-4 rounded-xl bg-amber-50 border border-amber-200/60 flex items-start gap-3">
        <span className="text-amber-600 font-bold text-sm shrink-0">
          💡 Pro-Tip:
        </span>
        <p className="text-xs text-amber-900 leading-relaxed">
          Keep your backup secret key written down in a secure physical
          location. If you ever lose or switch your mobile device, you will need
          it to recover access to your authenticator codes.
        </p>
      </div>
    </div>
  );
}
